import React, { Fragment, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Breadcrumbs } from "../../AbstractElements";
import { Button, Card, CardBody, CardHeader, Col, Row, Input, Label } from "reactstrap";
import { useTranslation } from "react-i18next";
import { getItemDetails, getSaltOnSearch, updateItemInfo } from "./PharmacyMedicineService";
import { useLoader } from "../../_helper/LoaderContext";
import { toast } from "react-toastify";
import { ArrowLeft } from "react-feather";
import { usePharmacy } from "../../_helper/PharmacyProvider";
import AsyncSelect from "react-select/async";
import { useDisableScrollOnFocus } from "../../Hooks/useDisableScrollOnFocus";

// Product forms (sorted alphabetically)
export const productForms = [
	{ id: 1, name: "Capsule", value: "capsule" },
	{ id: 2, name: "Capsule DR", value: "capsule_dr" },
	{ id: 3, name: "Capsule ER", value: "capsule_er" },
	{ id: 4, name: "Capsule SR", value: "capsule_sr" },
	{ id: 5, name: "Chewable Tablet", value: "chewable_tablet" },
	{ id: 6, name: "Cream", value: "cream" },
	{ id: 7, name: "Drop", value: "drop" },
	{ id: 8, name: "Dusting Powder", value: "dusting_powder" },
	{ id: 9, name: "Ear Drop", value: "ear_drop" },
	{ id: 10, name: "Effervescent Tablet", value: "effervescent_tablet" },
	{ id: 11, name: "Expectorant", value: "expectorant" },
	{ id: 12, name: "Gel", value: "gel" },
	{ id: 13, name: "Granule", value: "granule" },
	{ id: 14, name: "Infusion", value: "infusion" },
	{ id: 15, name: "Injection", value: "injection" },
	{ id: 16, name: "Kit", value: "kit" },
	{ id: 17, name: "Liquid", value: "liquid" },
	{ id: 18, name: "Lozenge", value: "lozenge" },
	{ id: 19, name: "Lotion", value: "lotion" },
	{ id: 20, name: "Nail Lacquer", value: "nail_lacquer" },
	{ id: 21, name: "Nasal Spray", value: "nasal_spray" },
	{ id: 22, name: "Ointment", value: "ointment" },
	{ id: 23, name: "Ophthalmic Solution", value: "ophthalmic_solution" },
	{ id: 24, name: "Oral Drop", value: "oral_drop" },
	{ id: 25, name: "Oral Gel", value: "oral_gel" },
	{ id: 26, name: "Oral Solution", value: "oral_solution" },
	{ id: 27, name: "Oral Suspension", value: "oral_suspension" },
	{ id: 28, name: "Paste", value: "paste" },
	{ id: 29, name: "Powder", value: "powder" },
	{ id: 30, name: "Powder for Infusion", value: "powder_for_infusion" },
	{ id: 31, name: "Powder for Injection", value: "powder_for_injection" },
	{ id: 32, name: "Powder for Oral Suspension", value: "powder_for_oral_suspension" },
	{ id: 33, name: "Rediuse Suspension", value: "rediuse_suspension" },
	{ id: 34, name: "Respule", value: "respule" },
	{ id: 35, name: "Solution", value: "solution" },
	{ id: 36, name: "Solution for Infusion", value: "solution_for_infusion" },
	{ id: 37, name: "Solution for Injection", value: "solution_for_injection" },
	{ id: 38, name: "Soap", value: "soap" },
	{ id: 39, name: "Spray", value: "spray" },
	{ id: 40, name: "Soft Gelatin Capsule", value: "soft_gelatin_capsule" },
	{ id: 41, name: "Strip", value: "strip" },
	{ id: 42, name: "Sublingual Tablet", value: "sublingual_tablet" },
	{ id: 43, name: "Suspension", value: "suspension" },
	{ id: 44, name: "Tablet", value: "tablet" },
	{ id: 45, name: "Tablet CR", value: "tablet_cr" },
	{ id: 46, name: "Tablet DT", value: "tablet_dt" },
	{ id: 47, name: "Tablet ER", value: "tablet_er" },
	{ id: 48, name: "Tablet FC", value: "tablet_fc" },
	{ id: 49, name: "Tablet MD", value: "tablet_md" },
	{ id: 50, name: "Tablet MR", value: "tablet_mr" },
	{ id: 51, name: "Tablet PR", value: "tablet_pr" },
	{ id: 52, name: "Tablet SR", value: "tablet_sr" },
	{ id: 53, name: "Tablet XL", value: "tablet_xl" },
	{ id: 54, name: "Tablet Combikit", value: "tablet_combikit" },
	{ id: 55, name: "Tablet Combipack", value: "tablet_combipack" },
	{ id: 56, name: "Dry Syrup", value: "dry_syrup" },
	{ id: 57, name: "Eye Drop", value: "eye_drop" },
	{ id: 58, name: "Eye Ointment", value: "eye_ointment" },
	{ id: 59, name: "Eye/Ear Drop", value: "eye_ear_drop" },
	{ id: 60, name: "Pastille", value: "pastille" },
	{ id: 61, name: "Other", value: "other" }
]

// Package types (sorted alphabetically)
export const packageTypes = [
	{ id: 1, name: "Ampoule", value: "ampoule" },
	{ id: 2, name: "Box", value: "box" },
	{ id: 3, name: "Bottle", value: "bottle" },
	{ id: 4, name: "Kit", value: "kit" },
	{ id: 5, name: "Packet", value: "packet" },
	{ id: 6, name: "Prefilled Syringe", value: "prefilled_syringe" },
	{ id: 7, name: "Pump Bottle", value: "pump_bottle" },
	{ id: 8, name: "Sachet", value: "sachet" },
	{ id: 9, name: "Strip", value: "strip" },
	{ id: 10, name: "Tube", value: "tube" },
	{ id: 11, name: "Vial", value: "vial" },
	{ id: 12, name: "Tablet", value: "tablet" },
	{ id: 13, name: "Other", value: "other" }
]


// OTC product forms (sorted alphabetically)
export const otcProductForms = [
	{ id: 1, name: "Balm / Stick", value: "balm" },
	{ id: 2, name: "Biscuits / Snacks", value: "snack" },
	{ id: 3, name: "Capsule", value: "capsule" },
	{ id: 4, name: "Chewable Tablet", value: "chewable_tablet" },
	{ id: 5, name: "Cream", value: "cream" },
	{ id: 6, name: "Drops", value: "drops" },
	{ id: 7, name: "Drinks / Beverages", value: "drink" },
	{ id: 8, name: "Effervescent Tablet", value: "effervescent_tablet" },
	{ id: 9, name: "Film / Oral Film", value: "oral_film" },
	{ id: 10, name: "Gel", value: "gel" },
	{ id: 11, name: "Gummy / Chewable Bar", value: "gummy" },
	{ id: 12, name: "Lotion", value: "lotion" },
	{ id: 13, name: "Lozenge / Troche", value: "lozenge" },
	{ id: 14, name: "Medicated Soap / Bar", value: "medicated_soap" },
	{ id: 15, name: "Nebulizer Solution", value: "nebulizer_solution" },
	{ id: 16, name: "Patch", value: "patch" },
	{ id: 17, name: "Powder", value: "powder" },
	{ id: 18, name: "Powder for Infusion / Injection (reconstitution)", value: "powder_for_infusion" },
	{ id: 19, name: "Powder for Suspension", value: "powder_for_suspension" },
	{ id: 20, name: "Syrup / Oral Solution", value: "syrup" },
	{ id: 21, name: "Soft Gelatin Capsule", value: "soft_gelatin_capsule" },
	{ id: 22, name: "Spray / Inhaler", value: "spray" },
	{ id: 23, name: "Suppository", value: "suppository" },
	{ id: 24, name: "Tablet", value: "tablet" },
	{ id: 25, name: "Other", value: "other" }
]

// OTC package types (sorted alphabetically)
export const otcPackageTypes = [
	{ id: 1, name: "Ampoule / Vial", value: "ampoule" },
	{ id: 2, name: "Blister Pack", value: "blister_pack" },
	{ id: 3, name: "Bottle", value: "bottle" },
	{ id: 4, name: "Box / Carton", value: "box" },
	{ id: 5, name: "Dropper + Bottle Combo", value: "dropper_combo" },
	{ id: 6, name: "Dropper Bottle", value: "dropper_bottle" },
	{ id: 7, name: "Foil Pack / Foil Sachet", value: "foil_pack" },
	{ id: 8, name: "Jar", value: "jar" },
	{ id: 9, name: "Jar with Screw Cap", value: "jar_screw_cap" },
	{ id: 10, name: "Pump Bottle", value: "pump_bottle" },
	{ id: 11, name: "Package", value: "package" },
	{ id: 12, name: "Roll-on / Pen Dispenser", value: "roll_on" },
	{ id: 13, name: "Sachet / Pouch", value: "sachet" },
	{ id: 14, name: "Spray Bottle / Aerosol", value: "spray_bottle" },
	{ id: 15, name: "Stick Pack / Stick Tube", value: "stick_pack" },
	{ id: 16, name: "Tube", value: "tube" },
	{ id: 17, name: "Tube with Nozzle", value: "tube_nozzle" },
	{ id: 18, name: "Other", value: "other" }
]

const UpdateMedicine = () => {
	const { mdcId } = useParams();
	const { t } = useTranslation();
	const history = useNavigate();
	const { setLoading } = useLoader();
	const { pharmacyPOS } = usePharmacy();

	// Track whether it's the first render
	const isFirstLoad = useRef(true);

	const [formData, setFormData] = useState({
		isOTC: 'false',
		brandName: "",
		saltName: "",
		saltId: "",
		manufacturer: "",
		package: "",
		productForm: "",
		quantity: "",
		regularPrice: "",
		sellingPrice: "",
		// discount: "",
		// tax: "",
		// taxType: "",
		barcode: "",
		note: "",
		// maxAlertQuantity: "",
		minAlertQuantity: "",
		// returnQuantity: "",
		// returnPrice: "",
		currency: "",
		tax: "",
	});

	const disableScrollProps = useDisableScrollOnFocus();
	const [errors, setErrors] = useState({});

	// ✅ Fetch item details on load
	useEffect(() => {
		const fetchItem = async () => {
			try {
				setLoading(true);
				const body = {
					itemId: mdcId,
					clinicId: pharmacyPOS?.hospital_id,
				}

				const res = await getItemDetails(body);

				// ✅ Access the nested item
				if (res?.data?.data) {
					const item = res.data.data;

					setFormData({
						isOTC: item.isOTC === true ? 'true' : 'false',
						brandName: item.brandName || "",
						saltName: item.saltName || "",
						manufacturer: item.manufacturer || "",
						package: item.package || "",
						productForm: item.productForm || "",
						quantity: item.packageQuantity || "",
						regularPrice: item.regularPrice || "",
						barcode: item.barcode || "",
						note: item.note || "",
						minAlertQuantity: item.minQuantityAlert || "",
						currency: item.currency || "INR",
						tax: item.tax || "",
					});
				}
			} catch (error) {
				console.error("Error fetching item:", error);
			} finally {
				setLoading(false);
			}
		};

		fetchItem();
	}, [mdcId]);

	// ✅ Load medicines on search
	const loadMedicines = async (inputValue) => {
		if (!inputValue || inputValue.length < 3) {
			return [];
		}

		const body = {
			search: inputValue,
		};

		try {
			setLoading(true);
			const response = await getSaltOnSearch(body);
			const medicines = response.data.data || [];

			// Build option labels to include product name, manufacturer, package and MRP
			return medicines.map((m) => ({
				value: m._id,
				label: `${m.name || ""}`,
				raw: m,
			}));
		} catch (error) {
			console.error("Error fetching medicines:", error);
			return [];
		} finally {
			setLoading(false);
		}
	};

	const [searchQuery, setSearchQuery] = useState('');

	const handleMedicineSearchChange = (newValue) => {
		setSearchQuery(newValue);
	};

	// ✅ When a medicine is selected
	const handleSelectMedicine = (selectedOption) => {
		if (!selectedOption) {
			// clear saltName if user clears selection
			setFormData((prev) => ({
				...prev,
				saltName: "",
			}));
			return;
		}

		const medicine = selectedOption.raw;

		// Update form fields based on medicine data
		setFormData((prev) => ({
			...prev,
			// product name -> brandName input
			isOTC: medicine.isOTC || prev.isOTC,
			saltName: medicine.name || prev.brandName,
			// salt name: prefer salt composition (without dosage) when available
			brandName: medicine.salt_composition || medicine.salt_composition_new || medicine.name || prev.saltName,
			saltId: medicine._id || prev.saltId,
			manufacturer: medicine.manufacturer || prev.manufacturer,
			package: medicine.package || prev.package,
			productForm: medicine.product_form || medicine.product_form || prev.productForm,
			regularPrice: medicine.mrp || prev.regularPrice,
			quantity: medicine.package_qty || prev.quantity,
			tax: medicine.tax || prev.tax,
		}));
	};

	// ✅ Handle input change
	const handleChange = (e) => {
		setFormData({ ...formData, [e.target.name]: e.target.value });
	};

	// ✅ Validation
	const validate = () => {
		let newErrors = {};
		if (!formData.isOTC) newErrors.isOTC = "Item type is required";
		if (!formData.brandName) newErrors.brandName = "Brand name is required";
		if (!formData.saltName) newErrors.saltName = "Salt name is required";
		if (!formData.manufacturer) newErrors.manufacturer = "Manufacturer is required";
		if (!formData.package) newErrors.package = "Packaging Type is required";
		if (!formData.productForm) newErrors.productForm = "Product form is required";
		if (!formData.quantity || formData.quantity <= 0) newErrors.quantity = "Quantity must be greater than 0";
		if (!formData.regularPrice || formData.regularPrice <= 0) newErrors.regularPrice = "Price must be greater than 0";
		// if (!formData.tax) newErrors.tax = "Tax is required";
		// if (!formData.taxType) newErrors.taxType = "Tax type is required";
		if (!formData.minAlertQuantity) newErrors.minAlertQuantity = "Minimum Alert quantity is required";
		// if (!formData.maxAlertQuantity) newErrors.maxAlertQuantity = "Maximum Alert quantity is required";
		if (!formData.currency) newErrors.currency = "Currency is required";
		return newErrors;
	};

	// ✅ Submit update
	const handleSubmit = async (e) => {
		e.preventDefault();
		const validationErrors = validate();
		if (Object.keys(validationErrors).length > 0) {
			setErrors(validationErrors);
			return;
		}
		try {
			setLoading(true);

			const body = {
				itemId: mdcId,
				clinicId: pharmacyPOS?.hospital_id,
				isOTC: formData.isOTC,
				brandName: formData.brandName,
				saltName: formData.saltName,
				medicationId: formData.saltId,
				manufacturer: formData.manufacturer,
				package: formData.package,
				productForm: formData.productForm,
				packageQuantity: formData.quantity,
				regularPrice: formData.regularPrice,
				barcode: formData.barcode,
				note: formData.note,
				minQuantityAlert: formData.minAlertQuantity,
				currency: formData.currency,
				tax: formData.tax,
			}
			const response = await updateItemInfo(body)
			toast.success(response.data.message);
			gotoList();
		} catch (error) {
			console.error("Error updating item:", error);
		} finally {
			setLoading(false);
		}
	};

	const gotoList = () => {
		history(`${process.env.PUBLIC_URL}/items`);
	}
	const isDark = document.body.classList.contains("dark-only");
	return (
		<Fragment>
			<Breadcrumbs mainTitle={t("Update Item")} parent={t("Dashboard")} title={t("Update Item")} />
			<Row className="mt-3">
				<Col sm={12}>
					<Card>
						<CardHeader className="d-flex justify-content-end align-items-center">
							<button
								className="btn btn-primary d-flex align-items-center"
								style={{ color: 'white', height: "40px" }}
								to=""
								onClick={gotoList}>
								<ArrowLeft className="me-2" /> {t("Back")}
							</button>
						</CardHeader>
						<CardBody>
							<form onSubmit={handleSubmit} className="row g-3">
								<div className="col-md-12">
									<div className="row">
										<label className="form-label fw-bold">{t("Item Type")}</label>
										<div className="col-md-2">
											<div className="radio radio-primary">
												<Input
													id="itemMedicine"
													type="radio"
													name="isOTC"
													value="false"
													checked={formData.isOTC === 'false'}
													disabled
													onChange={handleChange}
												/>
												<Label for="itemMedicine" className="ps-3">Medicine</Label>
											</div>
										</div>
										<div className="col-md-2">
											<div className="radio radio-primary">
												<Input
													id="itemOtc"
													type="radio"
													name="isOTC"
													value="true"
													checked={formData.isOTC === 'true'}
													disabled
													onChange={handleChange}
												/>
												<Label for="itemOtc" className="ps-3">OTC</Label>
											</div>
										</div>
										{errors.isOTC && <div className="text-danger">{errors.isOTC}</div>}
									</div>
								</div>
								{/* Brand Name */}
								<div className="col-md-4">
									<label className="form-label">Brand Name</label>
									{/* <input
										type="text"
										name="saltName"
										value={formData.saltName}
										onChange={handleChange}
										className="form-control"
									/> */}
									<AsyncSelect
										cacheOptions
										loadOptions={loadMedicines}
										defaultOptions={[]} // nothing until search
										value={
											formData.saltName
												? { label: formData.saltName, value: formData.saltName }
												: null
										}
										onChange={handleSelectMedicine}
										placeholder={t("Search & select an item")}
										onInputChange={handleMedicineSearchChange}
										noOptionsMessage={() =>
											searchQuery.length >= 3
												? t("No item found. Try another name.")
												: t("Type at least 3 characters to search...")
										}
										isClearable={true}
										theme={(theme) => ({
											...theme,
											colors: {
												...theme.colors,
												primary25: isDark ? "#373737" : "#e6f7ffff",

											},
										})}
									/>
									{errors.saltName && <div className="text-danger">{errors.saltName}</div>}
								</div>

								{/* Product Name */}
								<div className="col-md-4">
									<label className="form-label">{t("Salt Name / Composition")}</label>
									<input
										type="text"
										name="brandName"
										value={formData.brandName}
										onChange={handleChange}
										className="form-control"
									/>
									{errors.brandName && <div className="text-danger">{errors.brandName}</div>}
								</div>

								{/* Manufacturer */}
								<div className="col-md-4">
									<label className="form-label">Manufacturer</label>
									<input
										type="text"
										name="manufacturer"
										value={formData.manufacturer}
										onChange={handleChange}
										className="form-control"
									/>
									{errors.manufacturer && <div className="text-danger">{errors.manufacturer}</div>}
								</div>

								{/* Package */}
								<div className="col-md-4">
									<label className="form-label">Packaging Type</label>
									<input
										type="text"
										name="package"
										value={formData.package}
										onChange={handleChange}
										placeholder="e.g. Strip, Bottle, Box"
										className="form-control"
									/>
									{errors.package && <div className="text-danger">{errors.package}</div>}
								</div>
								{/* <div className="col-md-4">
									<label className="form-label">Package</label>
									<select
										name="package"
										value={formData.package}
										onChange={handleChange}
										className="form-select"
									>
										<option value="">Select Package</option>
										{packageTypes.map((pkg, index) => (
											<option key={index} value={pkg.name}>
												{pkg.name}
											</option>
										))}
									</select>
									{errors.package && <div className="text-danger">{errors.package}</div>}
								</div> */}

								{/* Product Form */}
								<div className="col-md-4">
									<label className="form-label">Product Form</label>
									<input
										type="text"
										name="productForm"
										value={formData.productForm}
										onChange={handleChange}
										className="form-control"
										placeholder="e.g. Tablet, Capsule, Syrup"
									/>
									{errors.productForm && <div className="text-danger">{errors.productForm}</div>}
								</div>
								{/* <div className="col-md-4">
									<label className="form-label">Product Form</label>
									<select
										name="productForm"
										value={formData.productForm}
										onChange={handleChange}
										className="form-select"
									>
										<option value="">Select Product Form</option>
										{productForms.map((form, index) => (
											<option key={index} value={form.name}>
												{form.name}
											</option>
										))}
									</select>
									{errors.productForm && <div className="text-danger">{errors.productForm}</div>}
								</div> */}

								{/* Quantity */}
								<div className="col-md-4">
									<label className="form-label">Product Quantity</label>
									<input
										type="number"
										name="quantity"
										min={1}
										value={formData.quantity}
										onChange={handleChange}
										className="form-control"
										{...disableScrollProps}
									/>
									{errors.quantity && <div className="text-danger">{errors.quantity}</div>}
								</div>

								{/* OKG MRP */}
								<div className="col-md-4">
									<label className="form-label" translate>Pack MRP</label>
									<input
										type="number"
										name="regularPrice"
										value={formData.regularPrice}
										onChange={handleChange}
										className="form-control"
										{...disableScrollProps}
									/>
									{errors.regularPrice && <div className="text-danger">{errors.regularPrice}</div>}
								</div>
								<div className="col-md-2">
									<label className="form-label">{t("Tax")}</label>
									<input
										type="number"
										name="tax"
										min={0}
										value={formData.tax}
										onChange={handleChange}
										className="form-control"
										{...disableScrollProps}
										placeholder={t("Set tax percentage")}
									/>
								</div>
								{/* Currency */}
								<div className="col-md-2">
									<label className="form-label">Currency</label>
									<select
										name="currency"
										value={formData.currency}
										onChange={handleChange}
										className="form-select"
										disabled
									>
										{/* <option value="" disabled>Select Currency</option> */}
										<option value="INR">INR (₹)</option>
										{/* <option value="ZiG">ZiG</option> */}
									</select>
									{errors.currency && <div className="text-danger">{errors.currency}</div>}
								</div>

								{/* Alert Quantity */}
								<div className="col-md-3">
									<label className="form-label">Minimum Stock Level (Alert)</label>
									<input
										type="number"
										name="minAlertQuantity"
										min={0}
										value={formData.minAlertQuantity}
										onChange={handleChange}
										className="form-control"
										{...disableScrollProps}
									/>
									{errors.minAlertQuantity && <div className="text-danger">{errors.minAlertQuantity}</div>}
								</div>
								{/* barcode */}
								<div className="col-md-4">
									<label className="form-label">{t("Barcode")}
										{/* <span className="text-danger">*</span> */}
									</label>
									<input
										type="text"
										name="barcode"
										value={formData.barcode}
										onChange={handleChange}
										className="form-control"
										placeholder={t("Enter barcode number")}
									// readOnly={!!selectedOption}
									/>
									{errors.barcode && <div className="text-danger">{errors.barcode}</div>}
								</div>
								{/* Note */}
								<div className="col-md-8">
									<label className="form-label">Note</label>
									<textarea
										name="note"
										value={formData.note}
										onChange={handleChange}
										className="form-control"
										placeholder="Add notes or special instructions"
									/>
								</div>

								{/* Buttons */}
								<div className="d-flex justify-content-end gap-2">
									<button className="btn btn-primary" type="submit">
										{t("Update Item")}
									</button>
									<Button
										variant="secondary"
										onClick={gotoList}
									>
										{t("Cancel")}
									</Button>
								</div>
							</form>
						</CardBody>
					</Card>
				</Col>
			</Row>
		</Fragment>
	);
};

export default UpdateMedicine;
